import { useMemo } from "react";
import { useParams } from "react-router";

import { SupervisorAccountIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Button from "components/Button";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";

import ESignAssigneeModificationTable from "entities/Memo/components/ESignAssigneeModificationTable";
import { useMemoSignatureRoleWithPrefetchedAssigneesList } from "entities/Memo/sdk";

export interface IMemoESignatureContentAssignmentOverview {
  setDrawerStepToNotificationEmail: () => void;
}

const MemoESignatureContentAssignmentOverview = ({
  setDrawerStepToNotificationEmail,
}: IMemoESignatureContentAssignmentOverview) => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const { data: memoSignatureRoles } =
    useMemoSignatureRoleWithPrefetchedAssigneesList({
      dealId,
      memoId,
    });

  const enableMemoSignatureProcessNextStep = useMemo(
    () =>
      Boolean(memoSignatureRoles?.some((role) => role.assignees.length === 0)),
    [memoSignatureRoles]
  );

  return (
    <Stack
      sx={{
        flex: 1,
        padding: 3,
      }}
      gap={2.5}
      overflow="hidden"
    >
      <Stack
        direction="row"
        gap={1}
        alignItems="flex-start"
        sx={{
          padding: theme.spacing(1.5, 1.5),
          backgroundColor: colors.gray10,
          borderRadius: "8px",
        }}
      >
        <SupervisorAccountIcon sx={{ width: "24px", height: "24px" }} />
        <Stack>
          <Text variant="text2" fontWeight={500}>
            There {memoSignatureRoles?.length === 1 ? "is" : "are"}{" "}
            {memoSignatureRoles?.length} signatory role
            {memoSignatureRoles?.length === 1 ? "" : "s"} set up for this
            document.
          </Text>
          <Text variant="text4" color={colors.gray80}>
            Signatory roles and the order in which they need to be signed are
            set up as part of the document template. Each role must have a
            contact assigned.
          </Text>
        </Stack>
      </Stack>
      <Stack flex={1} sx={{ overflowY: "auto" }}>
        <ESignAssigneeModificationTable memoId={memoId} dealId={dealId} />
      </Stack>
      <Tooltip
        title={
          enableMemoSignatureProcessNextStep
            ? "Please assign a contact to each role."
            : undefined
        }
      >
        <span>
          <Button
            disabled={enableMemoSignatureProcessNextStep}
            onClick={setDrawerStepToNotificationEmail}
            sx={{ width: "100%" }}
          >
            Next step
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default MemoESignatureContentAssignmentOverview;
